/* eslint-disable sonarjs/cognitive-complexity */
import { InfoPopoverProps, Stack, ToggleButton, ToggleButtonGroup } from '@landler/component-library';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { IndicatorFactType, IndicatorName, PlotReportFactLabelFilter } from '@/api/rest/resources/types/fact';
import { GeomodelServerGroupBy, GeomodelServerLabelFiltersOperation } from '@/api/rest/resources/types/organization';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { GraphTypeValue } from '@/components/Charts/hooks/useChartType';
import { useSetIndicatorHasData } from '@/components/IndicatorCard/IndicatorContextProvider';
import {
  CompositeGraph,
  CompositeGraphProps,
  ThresholdConfig,
} from '@/components/widgets/o/CompositeGraph/CompositeGraph';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { getFactsFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';
import { FactMapping, getFactMappingValues } from '@/utils/factMapping';
import { processFactsData } from '@/utils/processFactsData';

import { ChartHeading } from '../Charts/components/ChartHeading';
import { ChartZoomStateHook } from '../Charts/components/ChartZoom';
import { NUM_ITEMS_TYPE } from '../Charts/hooks/usePlotsForItemizedGraph';
import { GraphFact } from '../Charts/types';

export type GraphTileProps = {
  headerProps: GraphTileHeaderProps;
  contentProps: GraphTileContentProps;
  zoom?: ChartZoomStateHook['zoom'];
  setZoom?: ChartZoomStateHook['setZoom'];
  plotGroupId?: string | null;
  showEmpty?: boolean;
  graphType?: GraphTypeValue;
  plotRenderType?: (typeof NUM_ITEMS_TYPE)[keyof typeof NUM_ITEMS_TYPE];
  selectedBenchmark?: string | null;
};

type GraphTileHeaderProps = {
  title: string;
  popoverProps?: Partial<InfoPopoverProps>;
  hideChartHeader?: boolean;
};

type GraphTileContentProps = {
  graphFactType?: IndicatorFactType | null;
  sourceRef?: string;
  groupByLabel?: string;
  dateGroupingFormat?: 'yyyy' | 'mm-yyyy';
  transposeCategoriesToRows?: boolean;
  keepZeroCount?: boolean;
  group_by?: GeomodelServerGroupBy[];
  label_filters_operation?: GeomodelServerLabelFiltersOperation;
  labels?: PlotReportFactLabelFilter;
  limit?: number;
  filterByLabels?: Record<string, string>[];
  filterByLabelName?: string;
  filterByLabelValue?: string;
  toggleOptions?: {
    label: string;
  };
  thresholdsConfig?: {
    /* Fact mapping to draw reference lines from fact data */
    factMappings?: FactMapping[];
  };
  yAxisProps?: {
    /* Fact mapping to control Y axis domain using facts */
    factMappings?: FactMapping[];
  };
  /* Fact mapping to filter fact data when request contains same fact names with different label sets */
  dataFactMapping?: FactMapping;
} & CompositeGraphProps;

export const GraphTile = ({ headerProps, contentProps, plotGroupId, selectedBenchmark }: GraphTileProps) => {
  const { setIndicatorGraphData } = useSetIndicatorHasData();
  const organizationId = useOrganizationId();

  const fact = contentProps.graphFactType ?? IndicatorName.unknown;
  const {
    groupByLabel,
    dateGroupingFormat,
    transposeCategoriesToRows,
    keepZeroCount,
    filterByLabels,
    filterByLabelName,
    filterByLabelValue,
    toggleOptions,
    thresholdsConfig,
    yAxisProps: yAxisPropsRaw,
    dataFactMapping,
  } = contentProps;

  const geomodelServerOptions = {
    group_by: contentProps?.group_by,
    label_filters_operation: contentProps?.label_filters_operation,
    source_ref: contentProps?.sourceRef,
    limit: contentProps?.limit,
  };

  const { [fact]: graphFact } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    contentProps.graphFactType ?? IndicatorName.unknown,
    null,
    contentProps?.labels,
    plotGroupId,
    geomodelServerOptions,
  );
  const rawGraphData = graphFact?.value;
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, fact);
  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmark);

  const [selectedCategory, setSelectedCategory, toggleCategories] = useToggleOptions({ toggleOptions, rawGraphData });
  const toggleLabelName = toggleOptions?.label;

  const effectiveFilterByLabelName = toggleLabelName ?? filterByLabelName;
  const effectiveFilterByLabelValue = toggleLabelName && selectedCategory ? selectedCategory : filterByLabelValue;

  const hasFilteringOrGrouping =
    groupByLabel || Array.isArray(filterByLabels) || effectiveFilterByLabelName || effectiveFilterByLabelValue;
  let graphData =
    Array.isArray(rawGraphData) && hasFilteringOrGrouping
      ? processFactsData({
          facts: rawGraphData,
          groupByLabel,
          countFromLabel: undefined,
          dateGroupingFormat,
          transposeCategoriesToRows,
          keepZeroCount,
          filterByLabels,
          filterByLabelName: effectiveFilterByLabelName,
          filterByLabelValue: effectiveFilterByLabelValue,
        })
      : rawGraphData;

  let thresholds = [] as ThresholdConfig[];
  if (thresholdsConfig?.factMappings) {
    thresholds = (getFactMappingValues({
      facts: (graphData ?? []) as Parameters<typeof getFactMappingValues>[0]['facts'],
      factMappings: thresholdsConfig.factMappings,
      outputKeys: ['label'],
    }) ?? []) as unknown as ThresholdConfig[];
  }

  let domain = yAxisPropsRaw?.domain as [number, number] | undefined;
  if (yAxisPropsRaw?.factMappings) {
    domain = getFactMappingValues({
      facts: (graphData ?? []) as Parameters<typeof getFactMappingValues>[0]['facts'],
      factMappings: yAxisPropsRaw.factMappings,
      outputKeys: [],
    }).flatMap((item) => (isNullish(item?.value) ? [] : [item?.value])) as unknown as [number, number];
  }

  if (dataFactMapping && !isNullish(graphData)) {
    graphData = getFactsFromFactArray(
      graphData as Parameters<typeof getFactsFromFactArray>[0],
      dataFactMapping.factName,
      dataFactMapping?.factLabels,
    );
  }

  const yAxisProps = {
    ...yAxisPropsRaw,
    domain: !isNullish(domain) && Array.isArray(domain) && domain?.length === 2 ? domain : undefined,
  };

  const compositeGraphProps = Object.fromEntries(
    Object.entries(contentProps).filter(
      ([key]) =>
        ![
          'graphFactType',
          'sourceRef',
          'groupByLabel',
          'dateGroupingFormat',
          'transposeCategoriesToRows',
          'keepZeroCount',
          'group_by',
          'label_filters_operation',
          'labels',
          'limit',
          'filterByLabels',
          'filterByLabelName',
          'filterByLabelValue',
          'toggleOptions',
          'thresholdsConfig',
          'dataFactMapping',
        ].includes(key),
    ),
  ) as CompositeGraphProps;

  const chartData = graphData?.length ? graphData : contentProps.data;
  const noChartData = !chartData || !chartData?.length;

  useEffect(() => {
    setIndicatorGraphData({ graphData: !noChartData });
  }, [noChartData, setIndicatorGraphData]);

  return (
    <Stack center>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        {headerProps && (
          <ChartHeading
            {...headerProps}
            heading={headerProps.title}
            className='w-full px-6 sm:px-0'
            indicator={fact as unknown as GraphTileType}
          />
        )}
        {toggleOptions && toggleCategories.length > 0 && (
          <Stack className='w-full' center centerMain>
            <ToggleButtonGroup value={selectedCategory} onChange={setSelectedCategory} size='small'>
              {toggleCategories.map((category) => (
                <ToggleButton key={category} value={category}>
                  {`${category.at(0)?.toUpperCase()}${category.slice(1)}`}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        )}
        {contentProps && (
          <CompositeGraph
            {...compositeGraphProps}
            data={chartData}
            thresholds={thresholds}
            yAxisProps={yAxisProps}
            customBenchmark={activeCustomBenchmark}
          />
        )}
      </Stack>
    </Stack>
  );
};

const useToggleOptions = ({
  toggleOptions,
  rawGraphData,
}: {
  toggleOptions?: {
    label: string;
  };
  rawGraphData: GraphFact[] | null | undefined;
}): [string | undefined, Dispatch<SetStateAction<string | undefined>>, string[]] => {
  const toggleLabelName = toggleOptions?.label;

  const toggleCategories = useMemo(() => {
    if (!toggleLabelName || !Array.isArray(rawGraphData)) {
      return [];
    }
    const values = rawGraphData
      .flatMap((fact) => fact.labels ?? [])
      .filter((label) => label.name === toggleLabelName)
      .map((label) => String(label.value));
    return Array.from(new Set(values));
  }, [rawGraphData, toggleLabelName]);

  const initialValue = toggleLabelName ? toggleCategories[0] : undefined;
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(initialValue);

  return [selectedCategory, setSelectedCategory, toggleCategories];
};
